import custom_camera_settings from '../utils/custom_camera_settings.json' with { type: 'json' };
import rest from '../utils/rest'

export default class RealSenseCameraApi {
    cameraApiUrl = 'http://localhost:8000/api';
    camera_settings = custom_camera_settings
    devices;
    selectedDevice;
    selectedDeviceSensors;
    currentStream;
    activeSessionId;

    configs = {
        depth: {
            stream_type: "depth",
            format: "Z16",
            resolution: {width: 1280, height: 720},
            framerate: 30,
            sensor_id: "",
        enable: true
        },
        color: {
            stream_type: "color",
            format: "RGB8",
            resolution: {width: 1280, height: 720},
            framerate: 30,
            sensor_id: "",
            enable: true
        },
        infrared1: {
            stream_type: "infrared-1",
            format: "y8",
            resolution: {width: 1280, height: 720},
            framerate: 30,
            sensor_id: "",
            enable: true
        },
        infrared2: {
            stream_type: "infrared-2",
            format: "y8",
            resolution: {width: 1280, height: 720},
            framerate: 30,
            sensor_id: "",
            enable: true
        }
    };
    
    filterConfigs = {
        none: null,
        EggVision1: {
            enable_decimation: false,
            decimation_magnitude: 2,
            enable_temporal_filter: true,
            temporal_smooth_alpha: 0.4,
            temporal_smooth_delta: 20.0,
            temporal_persistence_control: 7,
            enable_spatial_filter: true,
            spatial_smooth_alpha: 0.9,
            spatial_smooth_delta: 3.0,
            spatial_magnitude: 2,
            spatial_hole_fill: 3,
            depth_to_disparity: true,
            disparity_to_depth: true
        }
    }

    async init() {
        this.devices = await this.getDevices();
        this.selectedDevice = this.devices.length > 0 ? this.devices[0] : null;
        this.selectedDeviceSensors = this.selectedDevice ? await this.getSensors() : [];
        return this.selectedDevice != null
    }

    async dereference() {
        this.devies = [];
        this.selectedDevice = null;
        this.selectedDeviceSensors = [];
    }

    async loadSettings() {
        let json = JSON.stringify(this.camera_settings);
        const options = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json'},
            //body: JSON.stringify(body)
        };
        try {
            const success = await rest.call(`${this.cameraApiUrl}/devices/${this.selectedDevice.device_id}/advanced/loadsettings/?json=${json}`, options);
            return success;
        }
        catch(e) {
            throw new Error(`Unable to load custom advanced settings. (${e.message})`);
        }
    }

    async calibrate() {
        const options = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        };
        try {
            const status = await rest.call(`${this.cameraApiUrl}/devices/${this.selectedDevice.device_id}/calibrate`, options);
            return status;
        }
        catch(e) {
            throw new Error(`Calibration failed. (${e.message})`);
        }
    }

    async startStream(type, type2) {
        let body = { configs: [this.configs[type], this.configs[type2]], align_to: "color", apply_filters: true };
        const options = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        };
        try {
          this.currentStream = await rest.call(`${this.cameraApiUrl}/devices/${this.selectedDevice.device_id}/stream/start`, options);
        }
        catch(e) {
            throw new Error(`Stream failed to start. (${e.message})`);
        }
    }

    async stopStream() {
        if (this.currentStream) {
            const options = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            }
            try {
                await rest.call(`${this.cameraApiUrl}/devices/${this.selectedDevice.device_id}/stream/stop`, options);
                this.currentStream = null;
            }
            catch(e){
                throw new Error(`Local API threw an error attempting to stop the stream. It may need to be restarted. (${e.message})`);
            }
        }
    }

    async capture(base_file_name) {
        if(this.currentStream) {
            const options = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json'}
            }
            try {
                return await rest.call(`${this.cameraApiUrl}/devices/${this.selectedDevice.device_id}/capture?base_file_name=${base_file_name}`, options);
            }
            catch(e){
                throw new Error(`Failed to capture an image. (${e.message})`);
            }
        }
    }

    async loadImages(base_file_name) {
        const options = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json'}
        }
        try {
            return await rest.call(`${this.cameraApiUrl}/devices/get_images?base_file_name=${base_file_name}`, options);
        }
        catch(e){
            throw new Error(`Failed to get images. (${e.message})`)
        }
    }

    async markUpload(base_file_name) {
        const options = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json'}
        }
        try {
            return await rest.call(`${this.cameraApiUrl}/devices/mark_uploaded?base_file_name=${base_file_name}`, options);
        }
        catch(e){
            throw new Error(`Failed to mark directory uploaded. May need to manually delete. (${e.message})`);
        }
    }

    async deleteFiles(base_file_name) {
        const options = {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json'}
        }
        try {
            return await rest.call(`${this.cameraApiUrl}/devices/delete_files?base_file_name=${base_file_name}`,options);
        }
        catch(e){
            throw new Error(`Failed to automatically delete captured files. (${e.message})`);
        }
    }

    async deleteOldFiles() {
        const options = {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json'}
        }
        try {
            return await rest.call(`${this.cameraApiUrl}/devices/delete_old_files`, options);
        }
        catch(e){
            throw new Error(`An error occurred while deleting older files. Please manually delete to proceed. (${e.message})`);
        }
    }

    async getRtcOffer(type, type2) {
        if (!this.selectedDevice) {
            return null;
        }
        
        try {
            const offer = await rest.call(`${this.cameraApiUrl}/webrtc/offer`, {
                method: 'POST',
                body: JSON.stringify({ device_id: this.selectedDevice.device_id, stream_types: [this.configs[type].stream_type, this.configs[type2].stream_type] }),
                headers: { 'Content-Type': 'application/json' }
            });
            this.activeSessionId = offer.session_id;
            return offer;
        }
        catch(e) {
            throw new Error(`API did not send WebRTC offer. (${e.message})`);
        }
    }

    async getRtcAnswer(answer) {
        if (!this.selectedDevice) {
            return null;
        }
        try {
            const answers = await rest.call(`${this.cameraApiUrl}/webrtc/answer`, {
                method: 'POST',
                body: JSON.stringify({ sdp: answer.sdp, session_id: answer.session_id, type: answer.type }),
                headers: { 'Content-Type': 'application/json' }
            });
            return answers;
        }
        catch (e) {
            throw new Error(`API did not accept WebRTC answer. (${e.message})`);
        }
    }

    async closeRtcSession() {
        if (this.activeSessionId) {
            try {
                await rest.call(`${this.cameraApiUrl}/webrtc/sessions/${this.activeSessionId}`, {
                    method: 'DELETE'
                });
            }
            catch(e) {
                throw new Error(`Closing WebRTC session failed. API may need to restart. (${e.message})`);
            }
        }
    }

    async sendIceCandidate(candidate) {
        if (!this.selectedDevice) {
            return null;
        }
        try {
            return await rest.call(`${this.cameraApiUrl}/webrtc/ice-candidates`, {
                method: 'POST',
                body: JSON.stringify({ candidate: candidate.candidate, sdpMid: candidate.sdpMid, sdpMLineIndex: candidate.sdpMLineIndex, session_id: this.activeSessionId }),
                headers: { 'Content-Type': 'application/json' }
            });
        }
        catch(e) {
            throw new Error(`ICE candidate list not accepted. (${e.message})`);
        }
    }

    async getDevices() {
        try {
            const devices = await rest.call(`${this.cameraApiUrl}/devices`);
            return devices || [];
        }
        catch(e) {
            throw new Error(`Failed to get device list. (${e.message})`);
        }
    }

    async getSensors() {
        if (!this.selectedDevice) {
            return null;
        }
        try {
            const sensors = await rest.call(`${this.cameraApiUrl}/devices/${this.selectedDevice.device_id}/sensors`);
            const sensor =  sensors.filter(sensor => sensor.type === 'Stereo Module');
            this.configs['depth'].sensor_id = sensor.filter(sensor => sensor.type === 'Stereo Module')[0].sensor_id;
            this.configs['color'].sensor_id = sensors.filter(sensor => sensor.type === 'RGB Camera')[0].sensor_id;
            this.configs['infrared1'].sensor_id = sensor.filter(sensor => sensor.type === 'Stereo Module')[0].sensor_id;
            this.configs['infrared2'].sensor_id = sensor.filter(sensor => sensor.type === 'Stereo Module')[0].sensor_id;
            return sensors;
        }
        catch(e) {
            throw new Error(`Failed to get sensor list. (${e.message})`);
        }
    }

    async loadSensorPreset(sensorType, preset) {
        if (!this.selectedDevice) {
            return null;
        }
        try{
            const success = await rest.call(`${this.cameraApiUrl}/devices/${this.selectedDevice.device_id}/sensors/${this.configs[sensorType].sensor_id}/loadpreset?preset=${preset}`, {
                method:'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            return success;
        }
        catch(e){
            throw new Error(`Failed to load sensor preset. Results may be affected. (${e.message})`);
        }
    }

    async setFilterConfiguration(filterConfig) {
        if (!this.selectedDevice) {
            return null;
        }
        try {
            const body =  this.filterConfigs[filterConfig];
            const success = await rest.call(`${this.cameraApiUrl}/devices/${this.selectedDevice.device_id}/post_process_config`, {
                method: 'POST',
                body: JSON.stringify(body),
                headers: { 'Content-Type': 'application/json' }
            });
            return success;
        }
        catch(e) {
            throw new Error(`Failed to set post processing configuration. Egg detection will not perform well. (${e.message})`);
        }
    }

    async delFilterConfiguration() {
        if (!this.selectedDevice) {
            return null;
        }
        try {
            const success = await rest.call(`${this.cameraApiUrl}/devices/${this.selectedDevice.device_id}/post_process_config`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' }
            });
            return success;
        }
        catch(e) {
            throw new Error(`Failed to reset post processing configuration. (${e.message})`);
        }
    }

    async getDeviceName() {
        try {
            const deviceName = await rest.call(`${this.cameraApiUrl}/devices/hostname`);
            return deviceName;
        }
        catch(e) {
            throw new Error(`Unable to retrieve device name. (${e.message})`);
        }
    }
}
